import { Box, Button } from "@mui/joy";
import { useCallback, useEffect, useState } from "react";
import { ProgramContentType } from "data/BlockComponent";
import { setProgramContentData } from "logic/logics";
import { sendMessage } from "utils/webview";
import useGetEventFromApp from "hooks/useGetEventFromApp";
import AudioProgressBar from "./AudioProgressBar";
import { theme } from "styles/theme";
import { PauseIcon, PlayIcon } from "./AudioButtonStatus";
import { useRecoilState } from "recoil";
import { activeAudioPlayerAtom } from "recoil/activeAudioPlayerAtom";
import _ from "lodash";

type PlayerStatus =
  | "idle"
  | "loading"
  | "playing"
  | "buffering"
  | "paused"
  | "finished"
  | "error";

export default function AudioButtonWebview({
  audioInstanceKey,
  audioUrl,
  initialTime,
  setData,
  blockIndex,
  lineIndex,
  cellIndex,
  isCoach,
  title,
  artist,
  artwork,
  extraAudioUrl,
  readonly,
}: {
  audioInstanceKey: string;
  audioUrl: string;
  initialTime: number;
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  blockIndex: number;
  lineIndex: number;
  cellIndex: number;
  isCoach: boolean;
  title?: string;
  artist?: string;
  artwork?: string;
  extraAudioUrl?: string;
  readonly: boolean;
}) {
  const [playerStatus, setPlayerStatus] = useState<PlayerStatus>("idle");
  const [currentTime, setCurrentTime] = useState<number>(initialTime);
  const [duration, setDuration] = useState<number>(0);
  const [isSeeking, setIsSeeking] = useState(false);

  const [activeAudioKey, setActiveAudioKey] = useRecoilState(
    activeAudioPlayerAtom
  );
  const isActive = activeAudioKey === audioInstanceKey;
  const isPlaying = isActive && playerStatus === "playing";
  const showAsPlaying =
    isPlaying ||
    (isActive && playerStatus === "buffering") ||
    (isActive && playerStatus === "loading");
  const isLive = !!extraAudioUrl && duration > 0 && currentTime >= duration;

  useEffect(() => {
    if (isPlaying) {
      return;
    }

    setCurrentTime(initialTime);
  }, [initialTime, isPlaying]);

  useEffect(() => {
    if (!audioUrl || duration > 0) {
      return;
    }

    const audio = new Audio(audioUrl);
    audio.preload = "metadata";

    const handleMetadataLoaded = () => {
      if (isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const handleError = () => {
      console.error("오디오 메타데이터를 불러오는 데 실패했습니다.", audioUrl);
    };

    // 이벤트 리스너 등록
    audio.addEventListener("loadedmetadata", handleMetadataLoaded);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("loadedmetadata", handleMetadataLoaded);
      audio.removeEventListener("error", handleError);
      // audio 객체에 대한 참조를 정리하여 메모리 누수 방지
      audio.src = "";
    };
  }, [audioUrl, duration]);

  const requestLoadAndPlay = useCallback(() => {
    sendMessage(
      JSON.stringify({
        event: "audioAction",
        params: {
          action: "loadAndPlay",
          src: audioUrl,
          initialTime: currentTime,
          audioInstanceKey,
          title,
          artist,
          artwork,
          extraAudioUrl: extraAudioUrl,
        },
      })
    );
  }, [
    audioUrl,
    currentTime,
    playerStatus,
    audioInstanceKey,
    title,
    artist,
    artwork,
    extraAudioUrl,
  ]);

  const requestPause = useCallback(() => {
    sendMessage(
      JSON.stringify({
        event: "audioAction",
        params: { action: "pause", audioInstanceKey },
      })
    );
  }, [audioInstanceKey]);

  const requestResume = useCallback(() => {
    sendMessage(
      JSON.stringify({
        event: "audioAction",
        params: {
          action: "resume",
          src: audioUrl,
          initialTime: currentTime,
          audioInstanceKey,
          title,
          artist,
          artwork,
          extraAudioUrl,
        },
      })
    );
  }, [
    audioUrl,
    currentTime,
    playerStatus,
    audioInstanceKey,
    title,
    artist,
    artwork,
    extraAudioUrl,
  ]);

  const requestSeek = useCallback(
    (seekTime: number) => {
      sendMessage(
        JSON.stringify({
          event: "audioAction",
          params: { action: "seek", audioInstanceKey, seekTime },
        })
      );
    },
    [audioInstanceKey]
  );

  const parsedMessageFromApp = useGetEventFromApp();
  useEffect(() => {
    if (
      parsedMessageFromApp?.event !== "audioPlayer" ||
      parsedMessageFromApp.params?.audioInstanceKey !== audioInstanceKey
    ) {
      return;
    }
    const {
      status: statusFromApp,
      currentTime: timeFromApp,
      duration: durationFromApp,
      seekCompleted,
    } = parsedMessageFromApp.params;

    const newStatus = (
      statusFromApp === "stopped" ? "paused" : statusFromApp
    ) as PlayerStatus;
    setPlayerStatus(newStatus);

    if (seekCompleted) {
      setIsSeeking(false);
    }

    if (!isSeeking) {
      if (typeof timeFromApp === "number" && timeFromApp > 0) {
        setCurrentTime(timeFromApp);
      }
    }

    if (typeof durationFromApp === "number" && durationFromApp > 0) {
      setDuration(durationFromApp);
    }

    if (newStatus === "finished") {
      setActiveAudioKey(null);

      if (!isCoach) {
        setProgramContentData({
          setData,
          blockIndex,
          lineIndex,
          cellIndex,
          newlyAddedData: {
            currentTime,
            ...(currentTime > 0 && {
              id: "meditation_time",
              value: `${Math.floor((currentTime + 10 || 60) / 60)}`,
            }),
            isEnded: true,
          },
        });
      }
    }
  }, [
    parsedMessageFromApp,
    audioInstanceKey,
    isSeeking,
    setActiveAudioKey,
    isCoach,
    setData,
    blockIndex,
    lineIndex,
    cellIndex,
    currentTime,
    duration,
  ]);

  useEffect(() => {
    const isTimeToSave =
      isActive &&
      playerStatus === "playing" &&
      Math.floor(currentTime) % 5 === 0;

    if (isTimeToSave) {
      const isEnded =
        duration > 0 && currentTime > 0 && duration - 30 <= currentTime;
      setProgramContentData({
        setData,
        blockIndex,
        lineIndex,
        cellIndex,
        newlyAddedData: {
          currentTime,
          ...(currentTime > 0 && {
            id: "meditation_time",
            value: `${Math.floor((currentTime + 10 || 60) / 60)}`,
          }),
          ...(isEnded && { isEnded: true }),
        },
      });
    }
  }, [
    isActive,
    playerStatus,
    currentTime,
    duration,
    setData,
    blockIndex,
    lineIndex,
    cellIndex,
  ]);

  // useEffect(() => {
  //   return () => {
  //     if (activeAudioKey === audioInstanceKey) {
  //       sendMessage(
  //         JSON.stringify({
  //           event: "audioAction",
  //           params: { action: "gracefulStop", audioInstanceKey },
  //         })
  //       );
  //       setActiveAudioKey(null);
  //     }
  //   };
  // }, [activeAudioKey, audioInstanceKey, setActiveAudioKey]);

  const start = useCallback(() => {
    setActiveAudioKey(audioInstanceKey);
    if (playerStatus === "paused") {
      requestResume();
    } else {
      requestLoadAndPlay();
    }
  }, [setActiveAudioKey, audioInstanceKey, playerStatus, requestLoadAndPlay]);

  const stop = useCallback(() => {
    requestPause();
  }, [requestPause]);

  const handleSeekStart = () => setIsSeeking(true);
  const handleSeekChange = (newTime: number) => setCurrentTime(newTime);
  const handleSeekEnd = (newTime: number) => {
    requestSeek(newTime);
  };

  const setAudioEnded = useCallback(() => {
    setData((data) => {
      if (data) {
        const data_temp = _.cloneDeep(data);
        const currentContent =
          data_temp[blockIndex].lines[lineIndex][cellIndex].content || {};

        data_temp[blockIndex].lines[lineIndex][cellIndex].content = {
          ...currentContent,
          isEnded: true,
        };
        if (!_.isEqual(data, data_temp)) {
          return data_temp;
        }
        return data;
      }
      return data;
    });
  }, [setData, blockIndex, lineIndex, cellIndex]);

  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      if (!isCoach) setAudioEnded();
    }
  }, [isCoach, setAudioEnded]);

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  return (
    <Box>
      <AudioProgressBar
        currentTime={currentTime}
        duration={duration}
        onSeekStart={handleSeekStart}
        onSeekChange={handleSeekChange}
        onSeekEnd={handleSeekEnd}
        isPlaying={showAsPlaying}
        isLive={isLive}
      />
      <Button
        variant={showAsPlaying ? "Outlined" : "Solid"}
        color="primary"
        fullWidth
        onClick={() => (showAsPlaying ? stop() : start())}
        size={"lg"}
        sx={{
          fontSize: "16px",
          fontWeight: 700,
          justifyContent: "center",
          "& .MuiButton-startDecorator": {
            marginRight: 0,
            color: readonly
              ? theme.vars.palette.Content.SuperWeak
              : showAsPlaying
                ? theme.vars.palette.Content.Highlight
                : "white",
          },
          gap: 1,
        }}
        disabled={readonly}
        startDecorator={showAsPlaying ? <PauseIcon /> : <PlayIcon />}
      >
        {showAsPlaying ? "일시정지" : "재생하기"}
      </Button>
    </Box>
  );
}